import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const logoUrl = process.env.PUBLIC_URL + '/clickzLogo.png';

function LandingPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  const faq = useMemo(
    () => [
      {
        q: 'Is this a gallery like Pixieset / Pic-Time?',
        a: 'Nope. Clickz Fit is for simple delivery: upload files, add a title/message + cover photos, and send a clean download page link. No full gallery workflow.'
      },
      {
        q: 'What can I send?',
        a: 'Anything your client needs as files (typically JPEGs + a ZIP). You can also add up to a few cover photos to style the download page.'
      },
      {
        q: 'Do clients need an account?',
        a: 'No. They just open the link and download.'
      },
      {
        q: 'Is it secure?',
        a: 'Links are private and time-limited. You control what you share, and you can treat it like a “deliver once, look professional” flow.'
      },
      {
        q: 'Can I brand it for my studio?',
        a: 'Today: a clean Clickz-branded experience. Studio branding and custom domains can be added as premium options later.'
      },
      {
        q: 'Does it work on mobile?',
        a: 'Yes. The upload flow is already mobile-friendly and the landing below is fully responsive.'
      }
    ],
    []
  );

  useEffect(() => {
    // Basic SEO (without adding dependencies)
    document.title = 'Clickz Fit | Elegant File Transfer for Photographers';

    const ensureMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    ensureMeta(
      'description',
      'Clickz Fit is a simple, elegant file transfer service for photographers. Upload files, add cover photos and a message, and send clients a beautiful download link.'
    );

    // JSON-LD (Organization + WebSite)
    const scriptId = 'clickzfit-jsonld';
    const existing = document.getElementById(scriptId);
    if (!existing) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      script.text = JSON.stringify(
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "name": "Clickz Fit",
              "url": "https://clickz.fit",
              "logo": "https://clickz.fit/clickzLogo.png"
            },
            {
              "@type": "WebSite",
              "name": "Clickz Fit",
              "url": "https://clickz.fit"
            }
          ]
        },
        null,
        0
      );
      document.head.appendChild(script);
    }

    return () => {
      // keep JSON-LD, it’s fine to persist between routes
    };
  }, []);

  const goUpload = () => navigate('/upload');

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="lp-page">
      {/* Top nav */}
      <header className="lp-header">
        <div className="lp-shell lp-header-inner">
          <button className="lp-brand" onClick={() => scrollTo('top')} aria-label="Go to top">
            <img src={logoUrl} alt="Clickz Fit logo" className="lp-logo" />
            <div className="lp-brand-text">
              <div className="lp-brand-name">Clickz Fit</div>
              <div className="lp-brand-tag">File transfer for photographers</div>
            </div>
          </button>

          <nav className="lp-nav" aria-label="Primary">
            <button className="lp-nav-link" onClick={() => scrollTo('how')}>How it works</button>
            <button className="lp-nav-link" onClick={() => scrollTo('pricing')}>Pricing</button>
            <button className="lp-nav-link" onClick={() => scrollTo('faq')}>FAQ</button>
            <button className="lp-cta-small" onClick={goUpload}>Upload</button>
          </nav>
        </div>
      </header>
    
        <main id="top" className="lp-hero">
          <div className="lp-shell lp-hero-inner">
            <div className="lp-hero-left">
            <h1 className="lp-h1">
              Send photos like a pro.              
            </h1>

            <p className="lp-lead">
              Clickz Fit is a simple, elegant delivery page for photographers:
              upload files, add a message + cover photos, and send your client a clean download link.
            </p>

            <div className="lp-hero-actions">
              <button className="lp-cta" onClick={goUpload}>Start an upload</button>
              <button className="lp-ghost" onClick={() => scrollTo('how')}>See how it works</button>
            </div>

            <div className="lp-badges" aria-label="Highlights">
              <div className="lp-badge">No client accounts</div>
              <div className="lp-badge">Styled download page</div>
              <div className="lp-badge">Mobile-friendly</div>
              <div className="lp-badge">Private links</div>
            </div>
            <div className="lp-crosslink">  
                Looking for a full client experience?{' '}
                <a className="lp-crosslink-a" href="https://clickz.website" target="_blank" rel="noreferrer">
                Try Clickz Galleries
            </a>
            </div>
            </div>

            <div className="lp-hero-right" aria-hidden="true">
              <div className="lp-screenshots">
                <img
                  src={process.env.PUBLIC_URL + './images/screenshot1.png'}
                  alt="Screenshot 1"
                  className="lp-screenshot"
                />
                <img
                  src={process.env.PUBLIC_URL + './images/screenshot2.png'}
                  alt="Screenshot 2"
                  className="lp-screenshot"
                />
              </div>
            </div>
          </div>
          <div className="lp-hero-image">
            <img
              src={process.env.PUBLIC_URL + '/images/screenshot3.png'}
              alt="Main hero screenshot"
              className="lp-screenshot-hero"
            />
          </div>
        </main>

        {/* How it works */}
      <section id="how" className="lp-section">
        <div className="lp-shell">
          <h2 className="lp-h2">How it works</h2>
          <p className="lp-subtext">Three steps. Because we’re adults with limited patience.</p>

          <div className="lp-steps">
            <div className="lp-step">
              <div className="lp-step-num">1</div>
              <h3 className="lp-h3">Upload files</h3>
              <p className="lp-p">Choose the files you want to deliver. Add a title and a short message (optional).</p>
            </div>

            <div className="lp-step">
              <div className="lp-step-num">2</div>
              <h3 className="lp-h3">Add cover photos</h3>
              <p className="lp-p">Pick a few images to style the download page so it looks intentional, not accidental.</p>
            </div>

            <div className="lp-step">
              <div className="lp-step-num">3</div>
              <h3 className="lp-h3">Send the link</h3>
              <p className="lp-p">Your client opens the link and downloads. No accounts. No chaos.</p>
            </div>
          </div>

          <div className="lp-callout">
            <div className="lp-callout-title">Built for fast delivery</div>
            <div className="lp-callout-text">
              Perfect for quick client handoff, second shooters, vendors, previews, or “here’s everything” ZIP deliveries.
            </div>
            <button className="lp-cta" onClick={goUpload}>Upload now</button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="lp-section lp-section-alt">
        <div className="lp-shell">
          <h2 className="lp-h2">Pricing</h2>
          <p className="lp-subtext">
            Keep it simple: start free while you’re testing it. Add power features when you need them.
          </p>

          <div className="lp-pricing-table-wrapper">
            <table className="lp-pricing-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Free</th>
                  <th>Pro</th>
                  <th>Studio</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Storage limit (single upload)</td>
                  <td>Up to 25GB</td>
                  <td>Up to 50GB</td>
                  <td>Up to 50GB</td>
                </tr>
                <tr>
                  <td>Uploads/month</td>
                  <td>3</td>
                  <td>10</td>
                  <td>30</td>
                </tr>
                <tr>
                  <td>Download expiration</td>
                  <td>7 days</td>
                  <td>14 days <span title="Extendable">*</span></td>
                  <td>30 days <span title="Extendable">*</span></td>
                </tr>
                <tr>
                  <td>Extend expiration</td>
                  <td>-</td>
                  <td>Yes</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td>Dashboard (see uploads)</td>
                  <td>-</td>
                  <td>Yes</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td>Pricing</td>
                  <td>Free</td>
                  <td>$10/month</td>
                  <td>$25/month</td>
                </tr>
              </tbody>
            </table>
            <div className="lp-pricing-table-notes">
              <div>* Pro and Studio users can extend expiration for active links.</div>
              <div>All plans include: elegant download page, title + message, cover photos styling, private time-limited links.</div>
              <div>Studio: includes custom domain, white-label, team workflow, and custom limits. <a href="/terms">Terms & details</a></div>
            </div>
            <div className="lp-pricing-table-actions">
              <button className="lp-cta" onClick={goUpload}>Start Free</button>
              <button className="lp-ghost" onClick={() => scrollTo('faq')}>See FAQ</button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="lp-section">
        <div className="lp-shell">
          <h2 className="lp-h2">FAQ</h2>
          <p className="lp-subtext">Answers to the questions people will ask instead of reading the page.</p>

          <div className="lp-faq" role="list">
            {faq.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div className="lp-faq-item" key={item.q} role="listitem">
                  <button
                    className="lp-faq-q"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.q}</span>
                    <span className={`lp-faq-icon ${isOpen ? 'open' : ''}`} aria-hidden="true">+</span>
                  </button>
                  {isOpen && <div className="lp-faq-a">{item.a}</div>}
                </div>
              );
            })}
          </div>

          <div className="lp-bottom-cta">
            <h3 className="lp-h3">Ready to send a delivery link that doesn’t look like a random ZIP?</h3>
            <button className="lp-cta" onClick={goUpload}>Start an upload</button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-shell lp-footer-inner">
          <div className="lp-footer-left">
            <img src={logoUrl} alt="Clickz Fit logo" className="lp-footer-logo" />
            <div className="lp-footer-meta">
              <div className="lp-footer-name">Clickz Fit</div>
              <div className="lp-footer-note">Simple file transfer for photographers.</div>
            </div>
          </div>

          <div className="lp-footer-links">
            <button className="lp-footer-link" onClick={() => scrollTo('how')}>How it works</button>
            <button className="lp-footer-link" onClick={() => scrollTo('pricing')}>Pricing</button>
            <button className="lp-footer-link" onClick={() => scrollTo('faq')}>FAQ</button>
            <a className="lp-footer-link" href="/terms">Terms</a>
          </div>

          <a
            className="lp-footer-link"
            href="https://clickz.website"
            target="_blank"
            rel="noreferrer"
            >
            Clickz Galleries
            </a>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
